import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import NameFields from '../ui/NameFields';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { Alert } from '../ui/Alert';
import { humanizeEnum } from '../../utils/humanize';
import StatusPill from '../ui/StatusPill';
import useTableSort from '../ui/useTableSort';
import { Dialog, DialogHeader, DialogBody } from '../ui/Dialog';
import DropdownMenu from '../ui/DropdownMenu';
import Slideover from '../ui/Slideover';
import useTelemetry from '../../hooks/useTelemetry';
import { useToast } from '../ui';
import { settingsAPI } from '../../services/api';
import { useDealerWorkspace } from '../admin/DealerWorkspaceContext';
import { isSuperAdmin } from '../admin/SuperAdminGuard';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { isFieldTech } from '../../utils/isFieldTech';
import { NO_PAID_SEATS_MSG, isNoPaidSeatsError } from '../../utils/paidSeats';
import {
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import PageHeader from '../ui/PageHeader';
import {
  SEAT_TYPES,
  ROLES,
  PERMISSION_GROUPS,
  defaultPermissionsForRole,
  manageSeedPermissions,
  needsPermissionChecklist,
  computeSeatKind,
} from './usersPermissions';

// IDI-226: the seat number is the dealer's paid_seats (IDI sets it); the
// server refuses a new Paid seat past it with a 422 (D3). No number, no cap (D4).
const isNoSeatsError = isNoPaidSeatsError;

// IDI-682: list_users sends Cognito's Enabled as "active" / "inactive"; both
// "inactive" and "deactivated" mean the login is deactivated.
const isDeactivated = (u) => u.status === 'inactive' || u.status === 'deactivated';

// IDI-647: the client copy of the server's canManageUsers
// (backend-go/cmd/settings/main.go, IDI-625), which refuses any POST/PUT to
// users from a non-admin. A field tech is always refused; otherwise only the
// 'admin' role (exact, case-insensitive, trimmed, like HasRole) or an IDI
// super-admin may manage. Roles are user.groups when non-empty, else
// user.role split on commas. UX parity only: the server is the boundary.
const normRole = (r) => (typeof r === 'string' ? r.trim().toLowerCase() : '');

export function canManageUsers(user) {
  if (!user || typeof user !== 'object') return false;
  if (isFieldTech(user)) return false;
  if (isSuperAdmin(user)) return true;
  const groups = Array.isArray(user.groups) ? user.groups.map(normRole).filter(Boolean) : [];
  const roles = groups.length ? groups : String(user.role ?? '').split(',').map(normRole);
  return roles.includes('admin');
}

// IDI-202: exclude legacy roster rows already covered by the live users list
// (matched by email, case-insensitive) so a legacy row that has ALREADY been
// invited doesn't show twice. Extracted for direct testability.
export function filterRosterNotInvited(roster, liveUsers) {
  const liveEmails = new Set((liveUsers || []).map((u) => (u.email || '').toLowerCase()).filter(Boolean));
  return (roster || []).filter((r) => {
    const email = (r.email || '').toLowerCase();
    return !r.invited && (!email || !liveEmails.has(email));
  });
}

// IDI-354 (2026-09-28): the demo-user fixture list and its seeding helper were removed.
//
// They created four demo Cognito users in the viewing tenant on every mount
// of this page. See the mount effect below for the full rationale. Deleted
// rather than NODE_ENV-gated so no future edit can re-enable a write from a
// read-only settings page.


// Iter18 R2 §2.2: Permission checklist now uses toggles (peer-checked bg red).
// Same shape as the MarginsPage visibility toggle so the visual vocabulary stays
// consistent across Settings.
// IDI-694: disabled (a non-admin's Manage) shows every toggle read-only.
function PermissionChecklist({ permissions, onToggle, disabled = false }) {
  return (
    <div className="space-y-5">
      {PERMISSION_GROUPS.map((g) => (
        <div key={g.group}>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-muted)' }}>{g.group}</p>
          <div className="space-y-2">
            {g.permissions.map((p) => (
              <PermissionToggle
                key={p.key}
                label={p.label}
                checked={!!permissions[p.key]}
                onChange={() => onToggle(p.key)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PermissionToggle({ label, checked, onChange, disabled = false }) {
  return (
    <label className={clsx('flex items-center justify-between gap-3 text-sm', disabled ? 'cursor-not-allowed opacity-[var(--opacity-disabled)]' : 'cursor-pointer')}>
      <span style={{ color: 'var(--color-text-default)' }}>{label}</span>
      <span className="relative inline-flex items-center flex-shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="sr-only peer"
          style={{ accentColor: 'var(--color-action-primary)' }}
        />
        <span className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[color:var(--color-border)] after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[color:var(--color-action-primary)]" />
      </span>
    </label>
  );
}

// ─── Add User Modal (§5.1 add-user flow) ─────────────────────────────────────
function AddUserModal({ open, onClose, onSave, seatsAvailable, prefill }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', seat_type: 'viewer', role: 'salesperson' });
  const [permissions, setPermissions] = useState(defaultPermissionsForRole('salesperson'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [copied, setCopied] = useState(false);

  // IDI-202: when opened from the Legacy Roster's "Invite" action, pre-fill
  // name/email from the migrated row so the admin doesn't have to retype it.
  // Runs on `open` (not mount) since this modal instance is reused across
  // opens without unmounting.
  useEffect(() => {
    if (open && prefill) {
      setForm((f) => ({
        ...f,
        firstName: prefill.firstName || f.firstName,
        lastName: prefill.lastName || f.lastName,
        email: prefill.email || f.email,
      }));
    }
  }, [open, prefill]);

  // Field Tech is a role whose seat type is Viewer (§2)
  useEffect(() => {
    if (form.role === 'field_tech' && form.seat_type !== 'viewer') {
      setForm((f) => ({ ...f, seat_type: 'viewer' }));
    }
  }, [form.role, form.seat_type]);

  // IDI-805 D2: the checklist starts over at every opening and role change.
  // This modal stays mounted across openings, so `open` is the key.
  useEffect(() => {
    if (open) setPermissions(defaultPermissionsForRole(form.role));
  }, [open, form.role]);

  const showChecklist = needsPermissionChecklist(form.seat_type, form.role);

  const handleCopyPassword = async () => {
    if (createdCredentials?.temporaryPassword) {
      try {
        await navigator.clipboard.writeText(createdCredentials.temporaryPassword);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Fallback: select the text
      }
    }
  };

  const handleDone = () => {
    setCreatedCredentials(null);
    setCopied(false);
    setForm({ firstName: '', lastName: '', email: '', seat_type: 'viewer', role: 'salesperson' });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.email.trim()) {
      setError('First name and email are required');
      return;
    }
    if (form.seat_type === 'paid' && seatsAvailable != null && seatsAvailable <= 0) {
      setError(NO_PAID_SEATS_MSG);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const result = await onSave({
        ...form,
        // DEC-54: join first/last to single `name` for the backend (endpoint
        // accepts `name` as one string; split is UI-only until post-beta migration).
        name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        permissions: showChecklist ? permissions : undefined,
      });
      // If the backend returned a temporaryPassword, show the credentials card
      if (result?.temporaryPassword) {
        setCreatedCredentials(result);
      } else {
        // Legacy fallback: just close
        setForm({ firstName: '', lastName: '', email: '', seat_type: 'viewer', role: 'salesperson' });
        onClose();
      }
    } catch (err) {
      setError(isNoSeatsError(err) ? NO_PAID_SEATS_MSG : (err.message || 'Failed to add user'));
    } finally {
      setSaving(false);
    }
  };

  // ─── Credentials Created state ───
  if (createdCredentials) {
    return (
      <Dialog open={open} onClose={handleDone}>
        <DialogHeader onClose={handleDone}>Credentials Created</DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <Alert variant="success">User account created successfully.</Alert>

            {/* IDI-79: the backend also attempts to send a BIDIT-branded invite
                email with these same credentials. That send is best-effort and
                must never look like account creation failed, so it's surfaced
                here as a secondary notice rather than blocking this card. */}
            {createdCredentials.emailStatus === 'sent' ? (
              <Alert variant="info">An invite email was also sent to {createdCredentials.user?.email || form.email}.</Alert>
            ) : (
              <Alert variant="warning">
                The invite email could not be sent{createdCredentials.emailDetail ? ` (${createdCredentials.emailDetail})` : ''}. Share the credentials below with the user directly.
              </Alert>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <p className="text-sm text-gray-900">{createdCredentials.user?.email || form.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono select-all">
                  {createdCredentials.temporaryPassword}
                </code>
                <Button type="button" variant="secondary" size="sm" onClick={handleCopyPassword}>
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>

            <Alert variant="warning">
              This is a one-time login password. The user will be asked to set their own password on first sign-in. Share it securely.
            </Alert>

            <div className="flex justify-end pt-4 border-t border-[color:var(--color-border)]">
              <Button type="button" onClick={handleDone}>Done</Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} size="wide">
      <DialogHeader onClose={onClose}>Add User</DialogHeader>
      <DialogBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert variant="error">{error}</Alert>}

          <NameFields
            firstName={form.firstName}
            lastName={form.lastName}
            onFirstNameChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
            onLastNameChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
            idPrefix="user"
          />

          <div>
            <label htmlFor="user-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <Input id="user-email" type="email" value={form.email} required
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="john@company.com" />
          </div>

          {/* Seat type: Paid · Viewer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Seat Type</label>
            <div className="space-y-2">
              {SEAT_TYPES.map((type) => (
                <label key={type.value}
                  className={clsx('flex items-start gap-3 p-3 rounded-md border border-[color:var(--color-border)] cursor-pointer',
                    form.seat_type === type.value ? 'border-primary-500 bg-primary-50' : 'border-[color:var(--color-border)] hover:border-[color:var(--color-border)]')}>
                  <input type="radio" name="seat_type" value={type.value}
                    checked={form.seat_type === type.value}
                    onChange={(e) => {
                      const seat = e.target.value;
                      // IDI-767 D2: an Admin is always Paid, so Viewer drops Admin
                      // (the Role radios hide under Viewer and would keep it).
                      setForm((p) => ({
                        ...p,
                        seat_type: seat,
                        role: seat === 'viewer' && p.role === 'admin' ? 'salesperson' : p.role,
                      }));
                    }} className="mt-0.5"
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {type.label}
                      {type.value === 'paid' && seatsAvailable != null && <span className="ml-2 text-xs text-gray-400">{seatsAvailable} available</span>}
                    </div>
                    <div className="text-xs text-gray-500">{type.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Role: Admin · Salesperson · Field Tech */}
          {form.seat_type === 'paid' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <div className="space-y-2">
                {ROLES.filter((r) => r.value !== 'field_tech').map((role) => (
                  <label key={role.value}
                    className={clsx('flex items-start gap-3 p-3 rounded-md border border-[color:var(--color-border)] cursor-pointer',
                      form.role === role.value ? 'border-primary-500 bg-primary-50' : 'border-[color:var(--color-border)] hover:border-[color:var(--color-border)]')}>
                    <input type="radio" name="role" value={role.value}
                      checked={form.role === role.value}
                      onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} className="mt-0.5"
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{role.label}</div>
                      <div className="text-xs text-gray-500">{role.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Paid+non-admin → permission checklist, every toggle on (IDI-805) */}
          {showChecklist && (
            <div className="card p-3">
              <p className="text-sm font-medium text-gray-900 mb-2">Permissions</p>
              <PermissionChecklist
                permissions={permissions}
                onToggle={(k) => setPermissions((p) => ({ ...p, [k]: !p[k] }))}
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Sending Invite...' : 'Send Invite'}</Button>
          </div>
        </form>
      </DialogBody>
    </Dialog>
  );
}

// ─── Manage side-sheet (per-row ⋯ → Manage) ──────────────────────────────────
// Iter18 §2.3 SeatKindPill — visualizes computed "kind" in list rows.
const SEAT_KIND_STYLES = {
  admin:  { bg: 'var(--navy-50)',  fg: 'var(--navy-800)' },
  editor: { bg: 'var(--color-status-positive-bg)',  fg: 'var(--color-status-positive-text)' },
  sales:  { bg: 'var(--color-warning-bg)',  fg: 'var(--color-warning)' },
  viewer: { bg: 'var(--neutral-100)', fg: 'var(--color-text-muted)' },
};

function SeatKindPill({ user }) {
  const kind = computeSeatKind(user);
  const style = SEAT_KIND_STYLES[kind.value] || SEAT_KIND_STYLES.viewer;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: style.bg,
        color: style.fg,
        borderRadius: 'var(--radius-sm)',
      }}
      title={kind.description}
    >
      {kind.label}
    </span>
  );
}

// IDI-647: canEdit=false (a non-admin viewer) leaves the sheet a read-only
// view: no Save Permissions, so toggling the checklist changes nothing.
export function ManageUserSheet({ open, onClose, user, onSave, canEdit = true }) {
  // Iter18 §2.3 Q5 role-wipe fix: seed synchronously from the user prop so we never
  // race a stale-empty permissions object into a save. useEffect on [user] keeps the
  // sheet in sync when the operator switches between rows without closing it.
  const [permissions, setPermissions] = useState(() =>
    user ? manageSeedPermissions(user.permissions) : {}
  );
  const [saving, setSaving] = useState(false);
  // IDI-229 item 7: Save Permissions had no error handling at all -- a 409
  // (or any other failure) from the backend surfaced as nothing but an
  // unhandled promise rejection in the browser console. The sheet stayed
  // open with `saving` still true-looking-successful to the user, who had no
  // idea the save didn't happen.
  const toast = useToast();

  useEffect(() => {
    if (user) setPermissions(manageSeedPermissions(user.permissions));
  }, [user]);

  if (!user) return null;
  const isAdmin = user.role === 'admin';

  return (
    <Slideover open={open} onClose={onClose} title={`Manage ${user.name || user.email}`}>
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          {user.email} · <span className="capitalize">{humanizeEnum(user.seat_type)}</span> · <span className="capitalize">{humanizeEnum(user.role)}</span>
        </div>
        {isAdmin ? (
          <Alert variant="info">Admins have full access — no permission checklist.</Alert>
        ) : user.seat_type === 'viewer' ? (
          <Alert variant="info">Viewers are read-only — no permission choices.</Alert>
        ) : (
          <PermissionChecklist
            permissions={permissions}
            onToggle={(k) => setPermissions((p) => ({ ...p, [k]: !p[k] }))}
            disabled={!canEdit}
          />
        )}
        {canEdit && !isAdmin && user.seat_type === 'paid' && (
          <div className="flex justify-end pt-3 border-t border-[color:var(--color-border)]">
            <Button
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await onSave(user, permissions);
                  onClose();
                } catch (err) {
                  // IDI-229 item 7: surface the failure instead of letting it
                  // vanish as an unhandled promise rejection. Keep the sheet
                  // open on failure so the operator's edits aren't lost.
                  toast.error('Failed to save permissions', { description: isNoSeatsError(err) ? NO_PAID_SEATS_MSG : err?.message });
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
          </div>
        )}
      </div>
    </Slideover>
  );
}

function DeactivateUserModal({ open, onClose, user, onConfirm }) {
  const [confirming, setConfirming] = useState(false);
  const toast = useToast();
  // IDI-786: a refusal (own login, last admin: a 409 with the server's words)
  // shows as a toast and leaves the dialog open.
  const handleConfirm = async () => {
    try {
      setConfirming(true);
      await onConfirm(user);
      onClose();
    } catch (err) {
      toast.error('Failed to deactivate user', { description: err?.message });
    } finally {
      setConfirming(false);
    }
  };
  if (!user) return null;
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>Deactivate User</DialogHeader>
      <DialogBody>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Deactivate <strong>{user.name || user.email}</strong>? They lose access but their work-order and quote
            attributions are preserved.
          </p>
          <p className="text-sm text-gray-500">(Users are never hard-deleted.)</p>
          <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="button" variant="danger" onClick={handleConfirm} disabled={confirming}>
              {confirming ? 'Deactivating...' : 'Deactivate User'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

// ─── Resend invite: one-time credentials modal (IDI-79 review fix) ────────────
// A regenerated temporary password must never be shown in a toast: toasts are
// transient, can appear during shared-screen support calls without the
// viewer intending to capture a secret, and (unlike this modal) don't
// require an explicit dismiss action. Mirrors the guaranteed-fallback
// credentials card in AddUserModal above — same one-time-display,
// explicit-copy, explicit-"Done" pattern, just triggered from the row menu.
function ResendInviteCredentialsModal({ open, onClose, result }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!result?.temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: the password is still selectable text in the <code> block.
    }
  };

  if (!result) return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>Invite Resent</DialogHeader>
      <DialogBody>
        <div className="space-y-4">
          {result.emailStatus === 'sent' ? (
            <Alert variant="info">An invite email was sent to {result.email}.</Alert>
          ) : (
            <Alert variant="warning">
              The invite email could not be sent{result.emailDetail ? ` (${result.emailDetail})` : ''}. Share the new credentials below with the user directly.
            </Alert>
          )}

          {/* Backend (IDI-79 review fix) only includes temporaryPassword when
              emailStatus !== 'sent' — there's no reason for this secret to also
              transit the response when the user already has it in their inbox. */}
          {result.temporaryPassword && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Temporary Password</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono select-all">
                    {result.temporaryPassword}
                  </code>
                  <Button type="button" variant="secondary" size="sm" onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              <Alert variant="warning">
                This is a one-time login password. The user's previous password no longer works. Share it securely.
              </Alert>
            </>
          )}

          <div className="flex justify-end pt-4 border-t border-[color:var(--color-border)]">
            <Button type="button" onClick={onClose}>Done</Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

// ─── Row actions menu (⋯ = Manage + Deactivate only — no hard delete) ────────────
// IDI-200: retired the hand-rolled absolute-positioned menu (below) for the
// shared DropdownMenu atom (same PT-39a migration QuotesPage.js already did).
// The hand-rolled version was `position: absolute` relative to the row's own
// wrapping div -- harmless on its own, but Table.js wraps every table in
// `overflow-x-auto` for horizontal scroll on narrow viewports, and an
// absolutely-positioned descendant is clipped by that ancestor's overflow
// regardless of z-index. DropdownMenu's panel uses Headless UI's `anchor`
// prop (Floating UI), which positions relative to the viewport rather than
// the scrolling ancestor -- so it can never be clipped by Table.js's
// overflow-x-auto again, on this table or any other Table.js consumer.
export function RowMenu({ user, onManage, onDeactivate, onResendInvite }) {
  const items = [
    { label: 'Manage', onClick: () => onManage(user) },
    onResendInvite && !isDeactivated(user) && { label: 'Resend invite', onClick: () => onResendInvite(user) },
    // IDI-647: no onDeactivate (a non-admin viewer) → no Deactivate item.
    onDeactivate && !isDeactivated(user) && { label: 'Deactivate', variant: 'danger', onClick: () => onDeactivate(user) },
  ].filter(Boolean);
  return (
    <DropdownMenu
      ariaLabel={`Actions for ${user.name || user.email || user.id}`}
      items={items}
    />
  );
}

export default function UsersRolesPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deactivateUser, setDeactivateUser] = useState(null);
  const [manageUser, setManageUser] = useState(null);
  const [resendingUserId, setResendingUserId] = useState(null);
  const [resendResult, setResendResult] = useState(null);
  // IDI-202: the migrated legacy user roster — read-only, shown alongside the
  // live Cognito roster so a dealer's full legacy headcount is visible
  // instead of only whichever 1 person happened to already have a login.
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [rosterError, setRosterError] = useState(null);
  const [showRoster, setShowRoster] = useState(false);
  // Pre-fills AddUserModal when "Invite" is clicked on a roster row.
  const [inviteRosterRow, setInviteRosterRow] = useState(null);
  const { emit } = useTelemetry();
  const toast = useToast();
  // IDI-63: in a super-admin's dealer workspace, list only that dealer's
  // logins (scope=tenant) and offer no user creation or invites (IDI-252).
  const workspace = useDealerWorkspace();
  // IDI-647: hide what the IDI-625 server gate refuses a non-admin.
  const auth = useOptionalAuth();
  const canManage = canManageUsers(auth?.user);
  const ownUsername = auth?.user?.username;

  // IDI-226 (S1): the dealer's own paid-seat number; null = none (D4).
  const [paidSeats, setPaidSeats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => settingsAPI.getPaidSeats())
      .then((n) => { const v = Number(n); if (!cancelled) setPaidSeats(v > 0 ? v : null); })
      .catch(() => { if (!cancelled) setPaidSeats(null); });
    return () => { cancelled = true; };
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = workspace ? await settingsAPI.getTenantUsers() : await settingsAPI.getUsers();
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setUsers(list);
      return list;
    } catch (err) {
      setError('Failed to load users');
      return [];
    } finally {
      setLoading(false);
    }
  }, [workspace]);

  useEffect(() => {
    // IDI-354 (2026-09-28): the mount-time user-seeding call is REMOVED.
    //
    // It created four demo users in the VIEWER'S OWN tenant on every visit to
    // this page, as real Cognito accounts rather than local mocks. The
    // prior note said "test account has no pollution risk", but this page
    // ships to every dealer: the first real admin to open Settings > Users &
    // Roles got all four, and on a 3-paid-seat plan two of those seats were
    // consumed by fabricated users. That is also the "user in the account that
    // should not be there" reported on IDI-401.
    //
    // A page that DISPLAYS users must not CREATE users. If demo fixtures are
    // needed again they belong in a seeding script run deliberately against a
    // named tenant, never in a render path.
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IDI-202: load the migrated legacy roster alongside the live Cognito
  // roster. Independent try/catch/loading state so a roster failure never
  // blocks the (higher-priority) live user list above from rendering.
  useEffect(() => {
    (async () => {
      try {
        setRosterLoading(true);
        const list = await settingsAPI.getUserRoster();
        setRoster(Array.isArray(list) ? list : []);
      } catch (err) {
        setRosterError('Failed to load legacy user roster');
      } finally {
        setRosterLoading(false);
      }
    })();
  }, []);

  // PR-24 §2 (297_): count as "paid seat" any user whose seat_type is missing
  // OR 'paid' (i.e., not explicitly viewer/deactivated). Fixes the counter
  // reading 0 while a live Admin sits in the list below (migrated records
  // land without seat_type set).
  // IDI-226 D2: an unset Field Tech holds no paid seat (the Lambda counts alike).
  const paidUsed = users.filter((u) =>
    (u.seat_type === 'paid' || (!u.seat_type && !['field_tech', 'field_technician'].includes(u.role))) && !isDeactivated(u)
  ).length;
  const seatsAvailable = paidSeats ? Math.max(0, paidSeats - paidUsed) : null;

  const handleAddUser = async (form) => {
    const result = await settingsAPI.addUser(form);
    emit('settings.user_invited', { email: form.email, seat_type: form.seat_type, role: form.role });
    await fetchUsers();
    return result;
  };

  // IDI-202: open AddUserModal pre-filled from a legacy roster row. Does NOT
  // create the Cognito account itself — that only happens if/when the admin
  // completes and submits the (pre-filled) Add User form, exactly the same
  // as inviting a brand-new person.
  const handleInviteFromRoster = (rosterRow) => {
    setInviteRosterRow(rosterRow);
    setAddModalOpen(true);
  };

  const handleDeactivateUser = async (user) => {
    await settingsAPI.deactivateUser(user.id);
    emit('settings.user_deactivated', { user_id: user.id });
    await fetchUsers();
  };

  // IDI-79: recovery path for "the admin lost the one-time temp password and
  // there was no way to get it back" — regenerates the temp password and
  // re-sends the branded invite email. Guarded against double-clicks via
  // resendingUserId rather than disabling the whole row, since the menu is
  // already closed by the time this runs.
  //
  // Review fix (IDI-79): the regenerated temporaryPassword must NEVER be put
  // in a toast — toasts are transient and can appear on-screen during a
  // shared support call without the presenter intending to expose a secret.
  // On success it's surfaced only via resendResult, which drives the
  // explicit-dismiss ResendInviteCredentialsModal (same pattern as the
  // AddUserModal credentials card). A pure delivery failure (network/API
  // error, no password issued) is still a toast since there's no secret to
  // protect in that case.
  const handleResendInvite = async (user) => {
    if (resendingUserId) return;
    setResendingUserId(user.id);
    try {
      const result = await settingsAPI.resendInvite(user.id);
      emit('settings.user_invite_resent', { user_id: user.id });
      setResendResult({ ...result, email: user.email || user.id });
    } catch (err) {
      toast.error('Failed to resend invite', { description: err?.message });
    } finally {
      setResendingUserId(null);
    }
  };

  const handleSavePermissions = async (user, permissions) => {
    // Iter18 §2.3 Q5 role-wipe defensive posture:
    // Send the FULL user row (role + seat_type + email + name) alongside permissions,
    // so any backend that treats missing fields as "clear" cannot wipe them. This is a
    // per-row commit (no bulk PATCH), keeping sibling rows untouched.
    await settingsAPI.addUser({
      user_id: user.id,
      action_update: true,
      email: user.email,
      name: user.name,
      role: user.role,
      seat_type: user.seat_type,
      permissions,
    });
    emit('settings.user_updated', { user_id: user.id });
    await fetchUsers();
  };

  const handleRequestPaidSeat = async (user) => {
    if (seatsAvailable != null && seatsAvailable <= 0) {
      setError(NO_PAID_SEATS_MSG);
      return;
    }
    try {
      await settingsAPI.updateUserSeatType(user.id, 'paid');
    } catch (err) {
      setError(isNoSeatsError(err) ? NO_PAID_SEATS_MSG : (err?.message || 'Failed to update seat'));
      return;
    }
    emit('settings.user_updated', { user_id: user.id, seat_type: 'paid' });
    await fetchUsers();
  };

  // IDI-786 (D4): no Deactivate on your own row or on the dealer's last
  // enabled admin. UX only: the server refuses both with a 409.
  // A custom:role naming a super-admin counts as no role (IDI-648 D5).
  const enabledAdmins = users.filter((u) => !isDeactivated(u) && canManageUsers(u) && !isSuperAdmin(u));
  const canDeactivateRow = (u) =>
    canManage &&
    !(ownUsername && u.id === ownUsername) &&
    !(enabledAdmins.length === 1 && enabledAdmins[0].id === u.id);

  const filteredUsers = showDeactivated ? users : users.filter((u) => !isDeactivated(u));

  // IDI-202: exclude roster rows already covered by the live users list
  // (matched by email, case-insensitive) so a legacy row that has ALREADY
  // been invited doesn't show twice. `roster` rows also carry `invited`
  // from the backend's own Cognito cross-reference (belt-and-suspenders
  // against a stale/incomplete live `users` fetch). See filterRosterNotInvited
  // above for the extracted, directly-tested logic.
  const rosterNotInvited = filterRosterNotInvited(roster, users);

  // IDI-479: click-to-sort. Accessors mirror each cell's displayed value.
  const userSortColumns = [
    { key: 'name', type: 'text', value: (u) => u.name || u.email || null },
    { key: 'email', type: 'text', value: (u) => u.email || null },
    { key: 'seat', type: 'text', value: (u) => computeSeatKind(u)?.label || null },
    { key: 'role', type: 'text', value: (u) => humanizeEnum(u.role || 'salesperson') || null },
    { key: 'status', type: 'text', value: (u) => (isDeactivated(u) ? 'Deactivated' : u.status === 'pending' ? 'Pending' : 'Active') },
  ];
  const rosterSortColumns = [
    { key: 'name', type: 'text', value: (r) => r.name || null },
    { key: 'email', type: 'text', value: (r) => r.email || null },
    // Every roster row reads "Not Invited"; stays sortable for consistency.
    { key: 'status', type: 'text', value: () => 'Not Invited' },
  ];
  const { sortedRows: sortedUsers, getSortProps: getUserSortProps } = useTableSort(filteredUsers, userSortColumns);
  const { sortedRows: sortedRoster, getSortProps: getRosterSortProps } = useTableSort(rosterNotInvited, rosterSortColumns);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3"><div className="h-8 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} /><div className="h-32 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} /></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Users & Roles"
        subtitle="Manage team members, seat types, and permissions."
        actions={workspace || !canManage ? null : <Button onClick={() => { setInviteRosterRow(null); setAddModalOpen(true); }}><PlusIcon className="h-4 w-4 mr-2" />Add User</Button>}
      />

      {/* IDI-694: tell a read-only login why the controls are gone. */}
      {!canManage && <Alert variant="info" className="mb-4">Only an admin can add, change or deactivate users.</Alert>}

      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

      {/* License usage (§5.1) */}
      <p className="text-sm text-gray-500 mb-4">
        {paidSeats
          ? `${paidUsed} of ${paidSeats} seats used`
          : `${paidUsed} paid seat${paidUsed === 1 ? '' : 's'} used`}
      </p>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setShowDeactivated((p) => !p)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" aria-pressed={showDeactivated}>
          {showDeactivated ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          {showDeactivated ? 'Hide deactivated' : 'Show deactivated'}
        </button>
      </div>

      {/* DEC-55: adopt canonical <Table> (dark header). Previously hand-rolled
          with bg-gray-50/text-gray-500 — the only table in the app that diverged
          from the design-system token (var(--table-header-bg) / white text).
          Token comparison: see commit message. */}
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader {...getUserSortProps('name')}>Name</TableHeader>
            <TableHeader {...getUserSortProps('email')}>Email</TableHeader>
            <TableHeader {...getUserSortProps('seat')}>Seat</TableHeader>
            <TableHeader {...getUserSortProps('role')}>Role</TableHeader>
            <TableHeader {...getUserSortProps('status')}>Status</TableHeader>
            <TableHeader align="right" sticky>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" className="py-12 text-gray-500">No users found. Add your first team member to get started.</TableCell>
            </TableRow>
          ) : (
            sortedUsers.map((user) => (
              <TableRow key={user.id} className={clsx(isDeactivated(user) && 'opacity-60')}>
                <TableCell className="font-medium">{user.name || user.email}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell><SeatKindPill user={user} /></TableCell>
                <TableCell>
                  {humanizeEnum(user.role || 'salesperson')}
                  {canManage && user.seat_type === 'viewer' && user.role !== 'field_tech' && !isDeactivated(user) && (
                    <button onClick={() => handleRequestPaidSeat(user)}
                      className="ml-2 text-xs text-primary-600 hover:text-primary-800">Request paid seat</button>
                  )}
                </TableCell>
                <TableCell>
                  <StatusPill status={isDeactivated(user) ? 'inactive' : 'active'}>{isDeactivated(user) ? 'Deactivated' : user.status === 'pending' ? 'Pending' : 'Active'}</StatusPill>
                </TableCell>
                <TableCell align="right" sticky>
                  <RowMenu user={user} onManage={setManageUser} onDeactivate={canDeactivateRow(user) ? setDeactivateUser : undefined} onResendInvite={workspace || !canManage ? undefined : handleResendInvite} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* IDI-202: Legacy Roster — migrated entityType=user rows, excluding
          anyone already showing in the live table above (matched by email).
          Read-only list + a per-row "Invite" action that opens the SAME
          AddUserModal used for a brand-new person, pre-filled. Nothing here
          creates a Cognito account by itself. */}
      {!rosterLoading && rosterNotInvited.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowRoster((p) => !p)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 mb-3"
            aria-expanded={showRoster}
          >
            {showRoster ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            Legacy Roster ({rosterNotInvited.length} not yet invited)
          </button>
          {showRoster && (
            <>
              <p className="text-sm text-gray-500 mb-3">
                These are users from the legacy system that have not been invited to BIDIT yet.
                Inviting someone creates their account and sends them a real invite email.
              </p>
              {rosterError && <Alert variant="error" className="mb-4" onClose={() => setRosterError(null)}>{rosterError}</Alert>}
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader {...getRosterSortProps('name')}>Name</TableHeader>
                    <TableHeader {...getRosterSortProps('email')}>Email</TableHeader>
                    <TableHeader {...getRosterSortProps('status')}>Status</TableHeader>
                    <TableHeader align="right" sticky>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRoster.map((row) => (
                    <TableRow key={row.id} className={clsx(!row.isActive && 'opacity-60')}>
                      <TableCell className="font-medium">{row.name || '—'}</TableCell>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell><StatusPill status="inactive">Not Invited</StatusPill></TableCell>
                      <TableCell align="right" sticky>
                        {!workspace && canManage && <Button size="sm" variant="secondary" onClick={() => handleInviteFromRoster(row)}>Invite</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      )}

      <AddUserModal
        open={addModalOpen}
        onClose={() => { setAddModalOpen(false); setInviteRosterRow(null); }}
        onSave={handleAddUser}
        seatsAvailable={seatsAvailable}
        prefill={inviteRosterRow}
      />
      <DeactivateUserModal open={!!deactivateUser} onClose={() => setDeactivateUser(null)} user={deactivateUser} onConfirm={handleDeactivateUser} />
      <ManageUserSheet open={!!manageUser} onClose={() => setManageUser(null)} user={manageUser} onSave={handleSavePermissions} canEdit={canManage} />
      <ResendInviteCredentialsModal open={!!resendResult} onClose={() => setResendResult(null)} result={resendResult} />
    </div>
  );
}
